import React, { useState, useEffect, useCallback, useRef } from 'react';
import { api } from '../api';
import { Modal, ConfirmDialog, LoadingSpinner, clienteNome } from '../components/shared';

const tk = () => 'Bearer ' + (localStorage.getItem('sp_token') || '');

// ─── FORM CREDENZIALE ─────────────────────────────────────────────────────────
function CredenzialeForm({ initial = {}, clientiList, onSave, onClose, loading }) {
  const [d, setD] = useState({
    id_cliente: '', denominazione_libera: '', servizio: '', url: '',
    utente: '', password1: '', password2: '', password_iniziale: '',
    descrizione: '', note: '', ...initial, id_cliente: initial.id_cliente || '',
  });
  const set = (k, v) => setD(p => ({ ...p, [k]: v }));
  const [showPwd, setShowPwd] = useState(false);

  return (
    <form onSubmit={e => { e.preventDefault(); onSave(d); }}>
      <div className="form-grid">
        <div className="form-group">
          <label className="form-label">Cliente (anagrafica)</label>
          <select className="form-control" value={d.id_cliente} onChange={e => set('id_cliente', e.target.value)}>
            <option value="">— Nessuno / libero —</option>
            {clientiList.map(c => <option key={c.id} value={c.id}>{clienteNome(c)}</option>)}
          </select>
        </div>
        <div className="form-group">
          <label className="form-label">Denominazione libera</label>
          <input className="form-control" value={d.denominazione_libera || ''} placeholder="es. nome non in anagrafica"
            onChange={e => set('denominazione_libera', e.target.value)} />
        </div>
        <div className="form-group">
          <label className="form-label">Servizio *</label>
          <input className="form-control" value={d.servizio} required placeholder="es. ENTRATEL, ARUBA, Gmail…"
            onChange={e => set('servizio', e.target.value)} />
        </div>
        <div className="form-group">
          <label className="form-label">URL servizio</label>
          <input className="form-control" type="url" value={d.url || ''} placeholder="https://..."
            onChange={e => set('url', e.target.value)} />
        </div>
        <div className="form-group">
          <label className="form-label">Utente</label>
          <input className="form-control" value={d.utente || ''} onChange={e => set('utente', e.target.value)} />
        </div>
        <div className="form-group">
          <label className="form-label">Password</label>
          <div style={{ display:'flex', gap:6 }}>
            <input className="form-control" type={showPwd?'text':'password'} value={d.password1 || ''}
              onChange={e => set('password1', e.target.value)} />
            <button type="button" className="btn btn-sm" onClick={()=>setShowPwd(s=>!s)}>{showPwd?'🙈':'👁'}</button>
          </div>
        </div>
        <div className="form-group">
          <label className="form-label">Password 2 (es. PIN/secondo fattore)</label>
          <input className="form-control" type={showPwd?'text':'password'} value={d.password2 || ''}
            onChange={e => set('password2', e.target.value)} />
        </div>
        <div className="form-group">
          <label className="form-label">Password iniziale (storico)</label>
          <input className="form-control" value={d.password_iniziale || ''} onChange={e => set('password_iniziale', e.target.value)} />
        </div>
        <div className="form-group form-full">
          <label className="form-label">Descrizione</label>
          <input className="form-control" value={d.descrizione || ''} onChange={e => set('descrizione', e.target.value)} />
        </div>
        <div className="form-group form-full">
          <label className="form-label">Note</label>
          <textarea className="form-control" rows={3} value={d.note || ''} onChange={e => set('note', e.target.value)} />
        </div>
      </div>
      <div className="modal-footer">
        <button type="button" className="btn" onClick={onClose}>Annulla</button>
        <button type="submit" className="btn btn-primary" disabled={loading}>{loading ? '⏳' : 'Salva'}</button>
      </div>
    </form>
  );
}

// ─── RIGA CREDENZIALE ─────────────────────────────────────────────────────────
function RigaCredenziale({ cred, onEdit, onDelete, addToast }) {
  const [showPwd, setShowPwd] = useState(false);
  const [allegato, setAllegato] = useState(() => {
    if (!cred.allegato) return null;
    if (cred.allegato.includes('::')) {
      const [id, nome] = cred.allegato.split('::');
      return { id, nome, url: 'https://drive.google.com/file/d/' + id + '/view' };
    }
    return { id: cred.allegato, nome: cred.allegato.split('/').pop(), url: '/uploads/' + cred.allegato };
  });
  const fileRef = useRef(null);

  const nomeC = cred.denominazione_libera || clienteNome(cred) !== '—' ? (cred.denominazione_libera || clienteNome(cred)) : '—';

  const [extReady, setExtReady] = React.useState(false);

  React.useEffect(() => {
    const handler = (event) => {
      if (event.source !== window) return;
      if (event.data?.type === 'GESTIONALE_EXT_READY') setExtReady(true);
    };
    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, []);

  const copyAndOpen = async () => {
    if (extReady) {
      // Invia le credenziali all'estensione, che le autocompila sul prossimo sito aperto
      window.postMessage({
        type: 'GESTIONALE_EXT_SET_CRED',
        utente: cred.utente || '',
        password: cred.password1 || '',
        servizio: cred.servizio || '',
      }, '*');
      addToast('Credenziali pronte — apertura sito…', 'success');
    } else {
      try {
        await navigator.clipboard.writeText(`Utente: ${cred.utente || '—'}\nPassword: ${cred.password1 || '—'}`);
        addToast('Credenziali copiate negli appunti (estensione non rilevata)', 'success');
      } catch (e) {}
    }
    if (cred.url) window.open(cred.url, '_blank');
    else addToast('Nessun URL configurato per questo servizio', 'error');
  };

  const copyField = async (label, value) => {
    if (!value) return;
    try { await navigator.clipboard.writeText(value); addToast(label + ' copiata', 'success'); }
    catch (e) {}
  };

  const handleUpload = async (file) => {
    const fd = new FormData(); fd.append('file', file);
    const r = await fetch(`/api/credenziali/${cred.id}/allegato`, { method: 'POST', headers: { Authorization: tk() }, body: fd });
    const j = await r.json();
    if (j.success) { setAllegato({ id: j.data.driveId, nome: j.data.nome, url: j.data.url }); addToast('Allegato caricato', 'success'); }
  };

  const handleDeleteAllegato = async () => {
    await fetch(`/api/credenziali/${cred.id}/allegato`, { method: 'DELETE', headers: { Authorization: tk() } });
    setAllegato(null);
  };

  return (
    <div style={{ display:'grid', gridTemplateColumns:'160px 1fr 140px 140px 90px 80px 120px',
      gap:8, padding:'9px 14px', borderBottom:'1px solid var(--border)', alignItems:'center', fontSize:13 }}>
      <div style={{ fontWeight:600, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{cred.servizio}</div>
      <div style={{ color:'var(--text-muted)', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{nomeC}</div>
      <div style={{ display:'flex', alignItems:'center', gap:5, overflow:'hidden' }}>
        <span style={{ overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{cred.utente || '—'}</span>
        {cred.utente && <button onClick={()=>copyField('Utente', cred.utente)} title="Copia utente"
          style={{ background:'none', border:'none', cursor:'pointer', fontSize:12, flexShrink:0 }}>📋</button>}
      </div>
      <div style={{ display:'flex', alignItems:'center', gap:5 }}>
        <span style={{ fontFamily:'monospace', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
          {showPwd ? (cred.password1 || '—') : (cred.password1 ? '••••••••' : '—')}
        </span>
        {cred.password1 && (
          <>
            <button onClick={()=>setShowPwd(s=>!s)} title="Mostra/nascondi"
              style={{ background:'none', border:'none', cursor:'pointer', fontSize:12, flexShrink:0 }}>{showPwd?'🙈':'👁'}</button>
            <button onClick={()=>copyField('Password', cred.password1)} title="Copia password"
              style={{ background:'none', border:'none', cursor:'pointer', fontSize:12, flexShrink:0 }}>📋</button>
          </>
        )}
      </div>
      <div>
        {allegato ? (
          <div style={{ display:'flex', alignItems:'center', gap:4 }}>
            <a href={allegato.url} target="_blank" rel="noreferrer" style={{ fontSize:16 }}>📎</a>
            <button onClick={handleDeleteAllegato} style={{ background:'none', border:'none', cursor:'pointer', color:'var(--c-red)', fontSize:11 }}>×</button>
          </div>
        ) : (
          <button onClick={()=>fileRef.current?.click()} title="Allega file"
            style={{ background:'none', border:'1px dashed var(--border-strong)', borderRadius:4, cursor:'pointer', fontSize:13, padding:'2px 6px' }}>+</button>
        )}
        <input ref={fileRef} type="file" style={{ display:'none' }} onChange={e=>{ if(e.target.files[0]) handleUpload(e.target.files[0]); e.target.value=''; }} />
      </div>
      <div style={{ display:'flex', gap:4 }}>
        <button className="btn btn-sm" onClick={()=>onEdit(cred)} title="Modifica">✎</button>
        <button className="btn btn-sm btn-danger" onClick={()=>onDelete(cred)}>×</button>
      </div>
      <div>
        <button className="btn btn-sm btn-primary" onClick={copyAndOpen} disabled={!cred.url && !cred.utente}
          style={{ whiteSpace:'nowrap' }} title={extReady ? 'Autocompila ed apri' : 'Copia e apri (installa estensione per autocompilare)'}>
          {extReady ? '⚡ Apri e compila' : '🔗 Copia e apri'}
        </button>
      </div>
    </div>
  );
}

// ─── MAIN ─────────────────────────────────────────────────────────────────────
export default function Credenziali({ addToast }) {
  const [rows, setRows] = useState([]);
  const [clienti, setClienti] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [editItem, setEditItem] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [delItem, setDelItem] = useState(null);
  const [saving, setSaving] = useState(false);
  const importRef = useRef(null);
  const [importing, setImporting] = useState(false);

  useEffect(() => {
    api.getClienti({ limit: 300 }).then(d => setClienti(Array.isArray(d) ? d : (d?.rows || [])));
  }, []);

  const load = useCallback(() => {
    setLoading(true);
    fetch('/api/credenziali', { headers: { Authorization: tk() } })
      .then(r => r.json()).then(j => { if (j.success) setRows(j.data || []); })
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const filtered = rows.filter(r => {
    if (!q) return true;
    const txt = [r.servizio, r.denominazione_libera, clienteNome(r), r.utente, r.descrizione, r.note].join(' ').toLowerCase();
    return txt.includes(q.toLowerCase());
  });

  const handleSave = async (d) => {
    setSaving(true);
    try {
      const url = editItem ? `/api/credenziali/${editItem.id}` : '/api/credenziali';
      const method = editItem ? 'PUT' : 'POST';
      const res = await fetch(url, { method, headers: { 'Content-Type':'application/json', Authorization: tk() }, body: JSON.stringify(d) });
      const j = await res.json();
      if (!j.success) throw new Error(j.error);
      addToast(editItem ? 'Credenziale aggiornata' : 'Credenziale creata', 'success');
      setShowForm(false); setEditItem(null); load();
    } catch (e) { addToast(e.message, 'error'); }
    finally { setSaving(false); }
  };

  const handleDelete = async () => {
    try {
      await fetch(`/api/credenziali/${delItem.id}`, { method: 'DELETE', headers: { Authorization: tk() } });
      addToast('Credenziale eliminata', 'success');
      setDelItem(null); load();
    } catch (e) { addToast(e.message, 'error'); }
  };

  const handleImport = async (file) => {
    setImporting(true);
    try {
      const fd = new FormData(); fd.append('file', file);
      const res = await fetch('/api/credenziali/importa-excel', { method: 'POST', headers: { Authorization: tk() }, body: fd });
      const j = await res.json();
      if (!j.success) throw new Error(j.error);
      addToast(`${j.data.importati} credenziali importate`, 'success');
      load();
    } catch (e) { addToast(e.message, 'error'); }
    finally { setImporting(false); }
  };

  return (
    <div className="page-content">
      <div style={{ display:'flex', gap:8, flexWrap:'wrap', alignItems:'center', marginBottom:14 }}>
        <div style={{ fontSize:17, fontWeight:700, flex:1 }}>
          🔑 Credenziali
          <span style={{ marginLeft:8, padding:'2px 8px', borderRadius:10, fontSize:11, fontWeight:700,
            background:'var(--accent)22', color:'var(--accent)' }}>{rows.length}</span>
        </div>
        <div className="search-box" style={{ width:240 }}>
          <svg width="13" height="13" viewBox="0 0 16 16" fill="none"><circle cx="7" cy="7" r="5" stroke="currentColor" strokeWidth="1.5"/><path d="M11 11l3 3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg>
          <input placeholder="Cerca servizio, utente, cliente…" value={q} onChange={e => setQ(e.target.value)} />
        </div>
        <button className="btn btn-sm" onClick={() => importRef.current?.click()} disabled={importing}>
          {importing ? '⏳ Importazione…' : '📥 Importa Excel'}
        </button>
        <input ref={importRef} type="file" accept=".xlsx,.xls" style={{ display:'none' }}
          onChange={e => { if (e.target.files[0]) handleImport(e.target.files[0]); e.target.value=''; }} />
        <button className="btn btn-primary" onClick={() => setShowForm(true)}>+ Nuova credenziale</button>
      </div>

      <div style={{ background:'var(--surface)', border:'1px solid var(--border)', borderRadius:'var(--radius)', overflow:'hidden' }}>
        <div style={{ display:'grid', gridTemplateColumns:'160px 1fr 140px 140px 90px 80px 120px',
          gap:8, padding:'7px 14px', background:'var(--bg)', borderBottom:'2px solid var(--border)',
          fontSize:10.5, fontWeight:700, color:'var(--text-faint)', textTransform:'uppercase', letterSpacing:'0.4px' }}>
          <div>Servizio</div><div>Cliente</div><div>Utente</div><div>Password</div><div>Allegato</div><div>Azioni</div><div>Accesso</div>
        </div>
        {loading ? <div style={{ padding:32 }}><LoadingSpinner /></div>
        : filtered.length === 0 ? (
          <div className="empty-state" style={{ padding:32 }}>
            <div className="empty-state-icon">🔑</div>
            <div className="empty-state-title">Nessuna credenziale trovata</div>
          </div>
        ) : filtered.map(cred => (
          <RigaCredenziale key={cred.id} cred={cred} addToast={addToast}
            onEdit={c => { setEditItem(c); setShowForm(true); }}
            onDelete={setDelItem} />
        ))}
      </div>

      {showForm && (
        <Modal title={editItem ? 'Modifica credenziale' : 'Nuova credenziale'} onClose={() => { setShowForm(false); setEditItem(null); }}>
          <CredenzialeForm initial={editItem || {}} clientiList={clienti}
            onSave={handleSave} onClose={() => { setShowForm(false); setEditItem(null); }} loading={saving} />
        </Modal>
      )}
      {delItem && <ConfirmDialog message={`Eliminare la credenziale "${delItem.servizio}"?`}
        onConfirm={handleDelete} onCancel={() => setDelItem(null)} />}
    </div>
  );
}
